import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  type EditorState,
  type ElementNode,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
} from 'lexical';
import { FC, useEffect } from 'react';

import {
  $isNoteBlockNode,
  $isNoteChoiceValueNode,
  $isNoteFreeTextNode,
  $isNoteLineNode,
  $isNoteSectionNode,
  $isWordingNode,
} from '../../noteNodes';

type WordingMark = { key: NodeKey; named: boolean };

type LineWalk = {
  marks: WordingMark[];
  // A field follows: the wording block met next names it.
  open: boolean;
  blockId: string | null;
};

// How an editor's lines are read for the wording that names a field.
export type FieldNameReading = {
  $listLines: () => ElementNode[];
  isField: (node: LexicalNode) => boolean;
  isWording: (node: LexicalNode) => boolean;
  // The block a wording belongs to: only one block names a field; null
  // lets the whole run of wording name it.
  blockIdOf: (node: LexicalNode) => string | null;
  // What between the wording and a field keeps the wording from naming it.
  endsName: (node: LexicalNode) => boolean;
};

// A value of the note: a block's widget, a free text or a chosen value
// printed in the document.
const NOTE_FIELD_NAMES: FieldNameReading = {
  $listLines: () =>
    $getRoot()
      .getChildren()
      .filter($isNoteSectionNode)
      .flatMap((section) => section.getChildren())
      .filter($isNoteLineNode),
  isField: (node) =>
    ($isNoteBlockNode(node) && !node.isAside()) ||
    $isNoteFreeTextNode(node) ||
    $isNoteChoiceValueNode(node),
  isWording: $isWordingNode,
  blockIdOf: (node) => ($isWordingNode(node) ? node.getBlockId() : null),
  endsName: () => false,
};

// A line read from its end: the wording block right before a field, every
// run of it, names the field.
const $readLineWordings = (line: ElementNode, reading: FieldNameReading) =>
  line.getChildren().reduceRight<LineWalk>(
    (walk, node) => {
      if (reading.isField(node)) {
        return { ...walk, open: true, blockId: null };
      }
      if (!reading.isWording(node)) {
        return reading.endsName(node) ? { ...walk, open: false } : walk;
      }
      const blockId = reading.blockIdOf(node);
      const named =
        walk.open && (walk.blockId === null || walk.blockId === blockId);
      return {
        marks: [...walk.marks, { key: node.getKey(), named }],
        open: named,
        blockId,
      };
    },
    { marks: [], open: false, blockId: null },
  ).marks;

const markFieldNames = (
  editor: LexicalEditor,
  state: EditorState,
  reading: FieldNameReading,
) =>
  state
    .read(() =>
      reading.$listLines().flatMap((line) => $readLineWordings(line, reading)),
    )
    .forEach(({ key, named }) =>
      editor.getElementByKey(key)?.toggleAttribute('data-field-name', named),
    );

type Props = {
  // How this editor's lines read; the note's by default.
  reading?: FieldNameReading;
};

// The wording right before a field names it, and reads apart from the
// values: it is marked in the DOM, which the editor neither reads nor
// writes back.
export const NoteFieldNamePlugin: FC<Props> = ({
  reading = NOTE_FIELD_NAMES,
}) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    markFieldNames(editor, editor.getEditorState(), reading);
    return editor.registerUpdateListener(({ editorState }) =>
      markFieldNames(editor, editorState, reading),
    );
  }, [editor, reading]);

  return null;
};
